import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import { toast } from "react-toastify";

import Loader from "../../../common/Loader";
import NoDataMessage from "../../../common/NoDataMessage";
import MenuBar from "../../../components/MenuBar";
import {
  ContentLintReport,
  ContentLintRow,
  ContentQuestionRow,
} from "../../../common/contentTypes";
import * as Content from "../../../services/api/Content";
import { ConfirmModal } from "../ConfirmModal";
import { ContentTable } from "../ContentTable";
import { QuestionDetail } from "../QuestionDetail";
import { QuestionEditorModal } from "../QuestionEditorModal";
import { ContentLintIssue, MAX_CHOICE_LABEL } from "../questionForm";
import { categoryLabel, useCategories } from "../useCategories";

/** The tabs are exactly the editor's lint issues, so they share one union. */
type TabId = ContentLintIssue;

interface TabSpec {
  id: TabId;
  label: string;
  /** Column header over the tab-specific evidence. */
  detail: string;
  /** Alert tone: over-length content is unservable, the rest is degraded. */
  tone: "danger" | "warning";
  blurb: (count: number, rows: ContentLintRow[]) => React.ReactNode;
  bulkLabel: (count: number) => string;
}

const TABS: TabSpec[] = [
  {
    id: "overLengthLabels",
    label: "Over-length labels",
    detail: "Over-length label",
    tone: "danger",
    blurb: (count) => (
      <>
        <strong>{count}</strong> questions have an answer label over{" "}
        {MAX_CHOICE_LABEL} characters — they will never render in Slack and are
        effectively unservable.
      </>
    ),
    bulkLabel: (count) => `Delete all ${count}`,
  },
  {
    id: "duplicateChoices",
    label: "Duplicate choices",
    detail: "Duplicated choice",
    tone: "warning",
    blurb: (count) => (
      <>
        <strong>{count}</strong> questions have two identical answer choices —
        ambiguous or unwinnable.
      </>
    ),
    bulkLabel: (count) => `Delete all ${count}`,
  },
  {
    id: "duplicateTitles",
    label: "Duplicate titles",
    detail: "Copies",
    tone: "warning",
    blurb: (count, rows) => (
      <>
        <strong>{count}</strong> questions share an exact title with another —{" "}
        {redundantCount(rows)} redundant copies.
      </>
    ),
    // Deleting every row here would remove the question entirely, not just its
    // duplicates, so the label states what actually happens.
    bulkLabel: () => "Delete duplicates (keep one)",
  },
];

/** One copy of each duplicated title is the keeper; the rest are redundant. */
export const redundantCount = (rows: ContentLintRow[]) =>
  rows.length - new Set(rows.map((row) => row.title)).size;

/**
 * All but the first row of each duplicate-title group. Sorted by id so the
 * keeper is stable between runs rather than depending on Mongo's ordering.
 */
export const redundantRows = (rows: ContentLintRow[]) => {
  const byTitle = new Map<string, ContentLintRow[]>();
  for (const row of rows) {
    byTitle.set(row.title, [...(byTitle.get(row.title) ?? []), row]);
  }
  return [...byTitle.values()].flatMap((group) =>
    group
      .slice()
      .sort((a, b) => a.questionId.localeCompare(b.questionId))
      .slice(1),
  );
};

export const ContentQualityPage: React.FC = () => {
  const { categories } = useCategories();
  const [report, setReport] = useState<ContentLintReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<TabId>("overLengthLabels");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [expandedQuestion, setExpandedQuestion] =
    useState<ContentQuestionRow | null>(null);
  const [editing, setEditing] = useState<ContentQuestionRow | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{
    ids: string[];
    message: string;
  } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await Content.lintReport(MAX_CHOICE_LABEL));
    } catch (err) {
      toast.error(`Failed to load the lint report: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows: ContentLintRow[] = report ? report[tab] : [];
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const activeTab = TABS.find((entry) => entry.id === tab) as TabSpec;

  const counts = report
    ? {
        overLengthLabels: report.overLengthLabels.length,
        duplicateChoices: report.duplicateChoices.length,
        duplicateTitles: report.duplicateTitles.length,
      }
    : { overLengthLabels: 0, duplicateChoices: 0, duplicateTitles: 0 };

  /** Lint rows carry only an id, so the editor loads the full question by id. */
  const openEditor = async (row: ContentLintRow) => {
    try {
      const [match] = await Content.questionsByIds([row.questionId]);
      if (!match) {
        toast.error("Could not load that question — try the Questions screen");
        return;
      }
      setEditing(match);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  /** The expander shows the whole question, which the report does not carry. */
  const toggleExpanded = async (row: ContentLintRow) => {
    if (expanded === row.questionId) {
      setExpanded(null);
      setExpandedQuestion(null);
      return;
    }
    setExpanded(row.questionId);
    setExpandedQuestion(null);
    try {
      const [match] = await Content.questionsByIds([row.questionId]);
      setExpandedQuestion(match ?? null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const runDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    setDeleting(true);
    try {
      const { removed } = await Content.deleteQuestions({
        questionIds: pendingDelete.ids,
      });
      toast.success(`Deleted ${removed} question${removed === 1 ? "" : "s"}`);
      setPendingDelete(null);
      setSelected({});
      load();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setDeleting(false);
    }
  };

  const startBulkDelete = () =>
    setPendingDelete({
      ids: selectedIds,
      message: `Delete ${selectedIds.length} selected question${
        selectedIds.length === 1 ? "" : "s"
      }?`,
    });

  /**
   * On the duplicate-titles tab this spares one copy of each title — deleting
   * every row would remove the question itself, not just its duplicates.
   */
  const deleteWholeBucket = () => {
    const targets =
      tab === "duplicateTitles"
        ? redundantRows(rows)
        : (rows as ContentLintRow[]);
    setPendingDelete({
      ids: targets.map((row) => row.questionId),
      message:
        tab === "duplicateTitles"
          ? `Delete ${targets.length} redundant copies, keeping one of each title?`
          : `Delete all ${targets.length} questions in the “${activeTab.label}” list?`,
    });
  };

  const detailCell = (row: ContentLintRow) => {
    if (tab === "overLengthLabels") {
      return (
        <>
          <span className="text-warning">{row.detail}</span>
          {row.detail && (
            <Badge color="danger" className="ml-2">
              {Array.from(row.detail).length} chars
            </Badge>
          )}
        </>
      );
    }
    if (tab === "duplicateChoices") {
      return <Badge color="warning">{row.detail}</Badge>;
    }
    return row.copies ? <Badge color="danger">{row.copies}x</Badge> : null;
  };

  return (
    <div>
      <MenuBar />
      <div className="container-fluid mt-3 pb-5">
        <Card className="mb-0">
          <CardHeader>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h2 className="mb-1">Quality</h2>
                <div className="text-muted">
                  Content-guide lint over the live library. Fixing or deleting
                  here updates the main questions table.
                </div>
              </div>
              <Button size="sm" color="secondary" outline onClick={load}>
                Refresh
              </Button>
            </div>
          </CardHeader>
        </Card>

        <Nav tabs className="mt-2">
          {TABS.map((entry) => (
            <NavItem key={entry.id}>
              <NavLink
                href="#"
                active={tab === entry.id}
                onClick={(e) => {
                  e.preventDefault();
                  setTab(entry.id);
                  setSelected({});
                  setExpanded(null);
                }}
              >
                {entry.label} <Badge color="danger">{counts[entry.id]}</Badge>
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {rows.length > 0 && (
          <Alert
            color={activeTab.tone}
            className="d-flex justify-content-between align-items-center mb-0 rounded-0"
          >
            <span>{activeTab.blurb(rows.length, rows)}</span>
            <Button color="danger" size="sm" onClick={deleteWholeBucket}>
              {activeTab.bulkLabel(rows.length)}
            </Button>
          </Alert>
        )}

        {selectedIds.length > 0 && (
          <div className="my-2">
            <Button color="danger" size="sm" onClick={startBulkDelete}>
              Delete selected {selectedIds.length}
            </Button>
            <Button
              color="link"
              size="sm"
              className="text-muted"
              onClick={() => setSelected({})}
            >
              Clear selection
            </Button>
          </div>
        )}

        {loading ? (
          <Loader light />
        ) : rows.length === 0 ? (
          <NoDataMessage light componentName="quality issue" />
        ) : (
          <ContentTable className="table table-sm table-hover">
            <thead>
              <tr>
                <th style={{ width: "2rem" }} />
                <th style={{ width: "2rem" }} />
                <th>Title</th>
                <th style={{ width: "16rem" }}>Category</th>
                {tab === "duplicateTitles" && (
                  <th style={{ width: "7rem" }}>Difficulty</th>
                )}
                <th>{activeTab.detail}</th>
                <th style={{ width: "5rem" }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <React.Fragment key={`${row.questionId}-${row.detail ?? ""}`}>
                  <tr>
                    <td>
                      <Button
                        color="link"
                        size="sm"
                        className="p-0"
                        title="Show the whole question"
                        onClick={() => toggleExpanded(row)}
                      >
                        <i
                          className={`fas fa-caret-${
                            expanded === row.questionId ? "down" : "right"
                          }`}
                        />
                      </Button>
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        checked={!!selected[row.questionId]}
                        onChange={(e) =>
                          setSelected((prev) => ({
                            ...prev,
                            [row.questionId]: e.target.checked,
                          }))
                        }
                      />
                    </td>
                    <td>{row.title}</td>
                    <td>{categoryLabel(categories, row.categoryId)}</td>
                    {tab === "duplicateTitles" && (
                      <td>
                        <Badge
                          color={
                            row.difficulty === "easy"
                              ? "success"
                              : row.difficulty === "hard"
                              ? "danger"
                              : "warning"
                          }
                        >
                          {row.difficulty}
                        </Badge>
                      </td>
                    )}
                    <td>{detailCell(row)}</td>
                    <td>
                      <Button
                        size="sm"
                        color="secondary"
                        outline
                        onClick={() => openEditor(row)}
                      >
                        Fix
                      </Button>
                    </td>
                  </tr>
                  {expanded === row.questionId && (
                    <tr>
                      <td colSpan={tab === "duplicateTitles" ? 7 : 6}>
                        {expandedQuestion ? (
                          <QuestionDetail
                            question={expandedQuestion}
                            categoryLabel={categoryLabel(
                              categories,
                              expandedQuestion.categoryId,
                            )}
                          />
                        ) : (
                          <small className="text-muted">Loading…</small>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </ContentTable>
        )}
      </div>

      {editing && (
        <QuestionEditorModal
          question={editing}
          categories={categories}
          isOpen
          // Fixing from here is about this tab's issue, so the editor holds the
          // save until that issue is actually gone.
          issue={tab}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
          onFlagAndRemove={(question) => {
            setEditing(null);
            setPendingDelete({
              ids: [question.id],
              message: `Delete “${question.title}”?`,
            });
          }}
        />
      )}

      <ConfirmModal
        title="Delete questions?"
        isOpen={!!pendingDelete}
        busy={deleting}
        onCancel={() => setPendingDelete(null)}
        onConfirm={runDelete}
      >
        {pendingDelete?.message}{" "}
        {pendingDelete?.ids.length === 1 ? "It moves" : "They move"} to the{" "}
        <Badge color="secondary">deleted</Badge> state and{" "}
        {pendingDelete?.ids.length === 1 ? "drops" : "drop"} out of this lint
        view.
      </ConfirmModal>
    </div>
  );
};
